import React, { useState } from "react";
import { EyeOutlined, EditOutlined, CopyOutlined, DeleteOutlined, HistoryOutlined, FileOutlined } from '@ant-design/icons';
import styles from "./JobSwmsV2.module.css";
import SignHistoryContainer from "./SignHistoryContainer";
import SwmsWizard from "./SwmsWizard";

const dummySwmsCards = [
  {
    swms_form_id: "SWMS-2024-001",
    swms_form_name: "Excavation Safety Procedures",
    approval_status: "Approved",
    created_at: "2026-04-15",
    created_by: "John Smith",
    is_visible: 1,
    site_name: "Downtown Construction Site",
    category_tags: ["Excavation", "Safety"],
    approvers: ["John Smith", "Mary Jane"],
    associated_tasks: ["TASK-001: Excavation Work", "TASK-005: Site Preparation"]
  },
  {
    swms_form_id: "SWMS-2024-002",
    swms_form_name: "Deep Trench Operations",
    approval_status: "Draft",
    created_at: "2026-04-18",
    created_by: "Sarah Davis",
    is_visible: 0,
    site_name: "Downtown Construction Site",
    category_tags: ["Trenching"],
    approvers: [],
    associated_tasks: ["TASK-001: Excavation Work"]
  },
  {
    swms_form_id: "SWMS-2024-003",
    swms_form_name: "Concrete Foundation Procedure",
    approval_status: "Pending Approval",
    created_at: "2026-04-20",
    created_by: "Mike Johnson",
    is_visible: 0,
    site_name: "Downtown Construction Site",
    category_tags: ["Concrete", "Foundation"],
    approvers: ["John Smith"],
    associated_tasks: ["TASK-002: Concrete Pouring", "TASK-003: Foundation Work"]
  },
  {
    swms_form_id: "SWMS-2024-004",
    swms_form_name: "Formwork Safety Guidelines",
    approval_status: "Approved",
    created_at: "2026-04-10",
    created_by: "Emma Wilson",
    is_visible: 1,
    site_name: "North Park Development",
    category_tags: ["Formwork", "Installation"],
    approvers: ["John Smith", "Mary Jane", "Alex Brown"],
    associated_tasks: ["TASK-004: Formwork Installation", "TASK-006: Formwork Removal"]
  }
];

const SwmsCard = ({ swms }) => {
  const getStatusClass = (status) => {
    switch (status) {
      case 'Draft':
        return 'draft';
      case 'Pending Approval':
        return 'pending';
      case 'Approved':
        return 'approved';
      case 'Rejected':
        return 'rejected';
      default:
        return 'draft';
    }
  };

  const approvalStatus = swms.approval_status ? swms.approval_status : 'Draft';
  const statusClass = getStatusClass(approvalStatus);
  const isApproved = swms.approval_status === 'Approved';
  const isDraft = swms.approval_status === 'Draft';
  const isVisible = swms.is_visible === 1;
  const formName = swms.swms_form_name ? swms.swms_form_name : `SWMS ${swms.swms_form_id}`;
  const formId = swms.swms_form_id ? swms.swms_form_id : "ID-0000";
  const siteName = swms.site_name ? swms.site_name : "N/A";
  const createdDate = swms.created_at ? new Date(swms.created_at).toLocaleDateString() : "N/A";
  const createdBy = swms.created_by ? swms.created_by : "N/A";
  const associatedTasksCount = swms.associated_tasks ? swms.associated_tasks.length : 0;
  const hasTags = swms.category_tags && swms.category_tags.length > 0;
  const hasApprovers = swms.approvers && swms.approvers.length > 0;
  const statusColor = statusClass === 'approved' ? '#059669' : '#6B7280';
  const visibilityClass = isVisible ? styles.visible : '';
  const toggleActiveClass = isApproved ? styles.active : '';

  return (
    <div className={`${styles.card} ${styles[statusClass]}`}>
      {/* Header: Title + Status Badge */}
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <h3>{formName}</h3>
          <p className={styles.cardId}>{formId}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <span className={`${styles.statusBadge} ${styles[statusClass]}`}>
            {approvalStatus}
          </span>
          {/* Visibility Toggle */}
          <div className={`${styles.toggleSwitch} ${toggleActiveClass}`} />
        </div>
      </div>

      {/* Visibility Status */}
      <div className={`${styles.visibilityStatus} ${visibilityClass}`}>
        <span>Site visibility:</span>
        {isVisible ? (
          <span style={{ fontWeight: '600' }}>
            <EyeOutlined style={{ marginRight: "4px" }} />
            Visible to team
          </span>
        ) : (
          <span style={{ fontStyle: 'italic' }}>
            Available once Approved
          </span>
        )}
      </div>

      {/* Details Grid */}
      <div className={styles.detailsGrid}>
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Site</span>
          <span className={styles.detailValue}>{siteName}</span>
        </div>
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Created</span>
          <span className={styles.detailValue}>{createdDate}</span>
        </div>
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Created By</span>
          <span className={styles.detailValue}>{createdBy}</span>
        </div>
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Associated Tasks</span>
          <span className={styles.detailValue}>{associatedTasksCount}</span>
        </div>
      </div>

      {/* Category Tags */}
      {hasTags && (
        <div className={styles.tagContainer}>
          {swms.category_tags.map((tag, index) => (
            <span key={index} className={styles.tag}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Approvers */}
      {hasApprovers && (
        <div className={styles.approversSection}>
          <span className={styles.approversLabel}>Approvers:</span>
          <div className={styles.approversContainer}>
            {swms.approvers.map((approver, index) => (
              <div
                key={index}
                className={styles.approverAvatar}
                title={approver}
              >
                {approver.slice(0, 2).toUpperCase()}
              </div>
            ))}
          </div>
          <span className={styles.approversStatus}>✓ All approved</span>
        </div>
      )}

      {/* Footer */}
      <div className={styles.cardFooter}>
        <div className={styles.actionButtons}>
          <button className={styles.actionButton} title="View">
            <EyeOutlined />
          </button>
          <button className={styles.actionButton} title="Edit">
            <EditOutlined />
          </button>
          <button className={styles.actionButton} title="Copy">
            <CopyOutlined />
          </button>
          <button className={styles.actionButton} title="History">
            <HistoryOutlined />
          </button>
          {isDraft && (
            <button className={styles.actionButton} title="Delete">
              <DeleteOutlined />
            </button>
          )}
        </div>
        <span className={styles.statusText} style={{ color: statusColor }}>
          {approvalStatus}
        </span>
      </div>
    </div>
  );
};

export default function JobSwmsV2({ goToForm, jobId, sections, setSections }) {
  const [showWizard, setShowWizard] = useState(false);
  return (
    <div style={{ padding: "24px" }}>
      <hr className={styles.topDivider} />
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <button className={styles.createButton} onClick={() => setShowWizard(true)}>
          Create New SWMS
        </button>
      </div>

      {/* SWMS Cards Grid */}
      {dummySwmsCards.length > 0 ? (
        <div className={styles.cardGrid}>
          {dummySwmsCards.map((swms, index) => (
            <SwmsCard key={index} swms={swms} />
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <FileOutlined />
          </div>
          <h3 className={styles.emptyStateTitle}>No SWMS documents yet</h3>
          <p className={styles.emptyStateText}>Get started by creating your first Safe Work Method Statement for this job.</p>
          <button className={styles.createButton} onClick={() => setShowWizard(true)}>
            Create New SWMS
          </button>
        </div>
      )}

      {/* Sign History */}
      <hr className={styles.topDivider} style={{ marginTop: "32px" }} />
      <SignHistoryContainer jobId={jobId} />

      {/* Wizard */}
      {showWizard && (
        <SwmsWizard onClose={() => setShowWizard(false)} />
      )}
    </div>
  );
}
